/**
 * Example output (CurrentCashDataPoint[]):
 *
 * [
 *   { period: '19 May', balance: 8980800, date: new Date('2025-05-19') },
 *   { period: '26 May', balance: 9201500, date: new Date('2025-05-26') },
 *   { period: '02 Jun', balance: 10014793, date: new Date('2025-06-02') }
 * ]
 *
 * - period: label for x-axis (varies by granularity)
 * - balance: base-currency amount (same unit used across reporting)
 * - date: actual point date used for tooltips and ordering
 */

import { UTCDate } from '@date-fns/utc';
import {
  addDays,
  addMonths,
  differenceInCalendarMonths,
  format,
  getYear,
  isAfter,
  isBefore,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import type { AccountView } from '../../../types/entity-types';
import type { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import type { CurrentCashDataPoint } from '../components/charts/current-cash-chart';
import { getHistoricalCashBalance } from './get-historical-cash-balance';

export type HistoricalBalanceSeriesFilter = 'week' | 'month' | 'three_months' | 'six_months' | 'year' | 'all_time';

type GetHistoricalBalanceSeriesArgs = {
  accounts: AccountView[];
  baseCurrency: SupportedCountryCurrency;
  filter: HistoricalBalanceSeriesFilter;
  today?: Date;
};

// For x-axis tick labels
const formatLabel = (
  date: Date,
  granularity: 'daily' | 'weekly' | 'semi-monthly' | 'monthly',
  baseYear: number,
): string => {
  switch (granularity) {
    case 'monthly':
      return format(date, "MMM ''yy");
    case 'semi-monthly':
    case 'weekly':
    case 'daily':
    default:
      return getYear(date) === baseYear ? format(date, 'd MMM') : format(date, "d MMM ''yy");
  }
};

const generateDailyDates = (baseDate: Date, days: number): Date[] => {
  const start = addDays(startOfDay(baseDate), -(days - 1));
  const dates: Date[] = [];
  for (let i = 0; i < days; i++) {
    dates.push(addDays(start, i));
  }
  return dates;
};

const generateWeeklyDates = (baseDate: Date, weeks: number): Date[] => {
  const dates: Date[] = [];
  for (let i = weeks - 1; i >= 0; i--) {
    dates.push(addDays(startOfDay(baseDate), -i * 7));
  }
  return dates;
};

const generateSemiMonthlyDates = (baseDate: Date, monthsBack: number): Date[] => {
  const startWindow = subMonths(baseDate, monthsBack);
  const earliest = startOfDay(startWindow);
  const dates: Date[] = [];
  let cursor = startOfDay(baseDate);
  while (!isBefore(cursor, earliest)) {
    dates.unshift(cursor);
    cursor = addDays(cursor, -15);
  }
  return dates;
};

const generateMonthlyDates = (baseDate: Date, monthsBack: number): Date[] => {
  // Generate dates anchored to the same day-of-month as baseDate,
  // moving back in whole months. date-fns addMonths will clamp to month-end
  // for shorter months (e.g., 31st -> 30th/28th), which is acceptable.
  const dates: Date[] = [];
  for (let i = monthsBack - 1; i >= 0; i--) {
    dates.push(addDays(addMonths(baseDate, -i), 0));
  }
  return dates;
};

const generateAllTimeMonthlyDates = (baseDate: Date, accounts: AccountView[]): Date[] => {
  const allFspDates = accounts
    .flatMap((a) => (a.transactions || []).map((t) => (t as unknown as { fspDate?: string | Date | null })?.fspDate))
    .filter((d) => Boolean(d)) as (string | Date)[];

  if (allFspDates.length === 0) {
    // Default to last 12 months if no transactions present
    return generateMonthlyDates(baseDate, 12);
  }

  const earliestDate = allFspDates.reduce(
    (min, d) => {
      const dt = new UTCDate(d as unknown as string | number | Date);
      return isBefore(dt, min) ? dt : min;
    },
    new UTCDate(allFspDates[0] as unknown as string | number | Date),
  );

  const startMonth = startOfMonth(earliestDate);
  const endMonth = startOfMonth(baseDate);
  const total = Math.max(1, differenceInCalendarMonths(endMonth, startMonth) + 1);
  const dates: Date[] = [];
  // Anchor to the baseDate's day-of-month across the monthly timeline
  for (let i = total - 1; i >= 0; i--) {
    dates.unshift(addDays(addMonths(baseDate, -i), 0));
  }
  return dates;
};

export const getHistoricalBalanceSeries = ({
  accounts,
  baseCurrency,
  filter,
  today,
}: GetHistoricalBalanceSeriesArgs): CurrentCashDataPoint[] => {
  if (!accounts || accounts.length === 0) return [];

  const baseDate = startOfDay(today ? new UTCDate(today) : new UTCDate());

  let dates: Date[] = [];
  let granularity: 'daily' | 'weekly' | 'semi-monthly' | 'monthly' = 'daily';

  switch (filter) {
    case 'week':
      granularity = 'daily';
      dates = generateDailyDates(baseDate, 7);
      break;
    case 'month':
      granularity = 'weekly';
      dates = generateWeeklyDates(baseDate, 5); // ~past 30 days in 7-day steps
      break;
    case 'three_months':
      granularity = 'semi-monthly';
      dates = generateSemiMonthlyDates(baseDate, 3);
      break;
    case 'six_months':
      granularity = 'monthly';
      dates = generateMonthlyDates(baseDate, 6);
      break;
    case 'year':
      granularity = 'monthly';
      dates = generateMonthlyDates(baseDate, 12);
      break;
    case 'all_time':
      granularity = 'monthly';
      dates = generateAllTimeMonthlyDates(baseDate, accounts);
      break;
    default:
      granularity = 'daily';
      dates = generateDailyDates(baseDate, 7);
  }

  const data: CurrentCashDataPoint[] = dates.map((date) => {
    const target = date;
    const { historicalBalanceInBaseCurrency } = getHistoricalCashBalance({
      accounts,
      baseCurrency,
      targetDate: target,
    });
    return {
      period: formatLabel(date, granularity, getYear(baseDate)),
      balance: historicalBalanceInBaseCurrency || 0,
      date,
    };
  });

  // Ensure chronological order (already ensured in generators, but keep safe)
  data.sort((a, b) => (isAfter(a.date as Date, b.date as Date) ? 1 : -1));
  return data;
};
